import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

// Soft-touch contact CTAs (Call / WhatsApp / Email), driven by whatever
// contact info the record actually has. Two presentations share the same
// action-availability logic:
//   - variant="inline"  → a row of icon buttons (detail pages, roomy layouts)
//   - variant="menu"    → a compact "⋯" trigger that opens a dropdown (tables)
// The menu renders through a portal so it is never clipped by a scroll
// container such as `.table-wrap` (overflow-x: auto forces overflow-y: auto).

interface Props {
  phone?: string | null; // E.164, e.g. "+919876543210"
  email?: string | null;
  variant?: "inline" | "menu";
  /** Name used in accessible labels, e.g. "Call Priya". */
  name?: string | null;
  className?: string;
}

type Action = {
  key: "call" | "whatsapp" | "email";
  label: string;
  href: string;
  external?: boolean;
  icon: React.ReactNode;
};

function buildActions(phone?: string | null, email?: string | null): Action[] {
  const actions: Action[] = [];
  const trimmedPhone = phone?.trim();
  if (trimmedPhone) {
    const waNumber = trimmedPhone.replace(/[^\d]/g, "");
    actions.push({
      key: "call",
      label: "Call",
      href: `tel:${trimmedPhone}`,
      icon: <PhoneIcon />,
    });
    if (waNumber) {
      actions.push({
        key: "whatsapp",
        label: "WhatsApp",
        href: `https://wa.me/${waNumber}`,
        external: true,
        icon: <WhatsAppIcon />,
      });
    }
  }
  const trimmedEmail = email?.trim();
  if (trimmedEmail) {
    actions.push({
      key: "email",
      label: "Email",
      href: `mailto:${trimmedEmail}`,
      icon: <MailIcon />,
    });
  }
  return actions;
}

export function ContactActions({
  phone,
  email,
  variant = "inline",
  name,
  className = "",
}: Props) {
  const actions = buildActions(phone, email);
  if (actions.length === 0) return null;

  const suffix = name ? ` ${name}` : "";

  if (variant === "inline") {
    return (
      <div className={`contact-actions ${className}`.trim()}>
        {actions.map((a) => (
          <a
            key={a.key}
            className="btn btn-secondary btn-sm contact-action"
            href={a.href}
            {...(a.external
              ? { target: "_blank", rel: "noreferrer" }
              : {})}
            aria-label={`${a.label}${suffix}`}
            title={`${a.label}${suffix}`}
          >
            {a.icon}
            <span>{a.label}</span>
          </a>
        ))}
      </div>
    );
  }

  return <ContactMenu actions={actions} suffix={suffix} className={className} />;
}

function ContactMenu({
  actions,
  suffix,
  className,
}: {
  actions: Action[];
  suffix: string;
  className: string;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null
  );
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Position after the menu is in the DOM so we can measure it and flip
  // above / clamp into the viewport when there isn't room below (e.g. rows
  // near the bottom edge). While coords is null the menu renders off-screen
  // and hidden purely so this pass can read its dimensions.
  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }
    const trigger = triggerRef.current;
    const menu = menuRef.current;
    if (!trigger || !menu) return;

    const gap = 4;
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const rect = trigger.getBoundingClientRect();
    const mw = menu.offsetWidth;
    const mh = menu.offsetHeight;

    // Prefer below; flip above if it would overflow the bottom and there's
    // more room up top.
    let top = rect.bottom + gap;
    if (top + mh > vh - margin && rect.top - gap - mh >= margin) {
      top = rect.top - gap - mh;
    }
    top = Math.max(margin, Math.min(top, vh - mh - margin));

    // Right-align to the trigger's right edge, clamped into the viewport.
    let left = rect.right - mw;
    left = Math.max(margin, Math.min(left, vw - mw - margin));

    setCoords({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        !triggerRef.current?.contains(t) &&
        !menuRef.current?.contains(t)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    // Fixed-position coords go stale on scroll/resize — close rather than
    // chase the trigger around.
    const onReflow = () => setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onReflow, true);
    window.addEventListener("resize", onReflow);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onReflow, true);
      window.removeEventListener("resize", onReflow);
    };
  }, [open]);

  return (
    <span className={`contact-menu ${className}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        className="contact-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Contact actions${suffix}`}
        title={`Contact actions${suffix}`}
        onClick={() => setOpen((v) => !v)}
      >
        <DotsIcon />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              className="contact-menu-list"
              role="menu"
              style={
                coords
                  ? { top: coords.top, left: coords.left }
                  : { top: 0, left: 0, visibility: "hidden" }
              }
            >
              {actions.map((a) => (
                <a
                  key={a.key}
                  role="menuitem"
                  className="contact-menu-item"
                  href={a.href}
                  {...(a.external
                    ? { target: "_blank", rel: "noreferrer" }
                    : {})}
                  onClick={() => setOpen(false)}
                >
                  {a.icon}
                  <span>{a.label}</span>
                </a>
              ))}
            </div>,
            document.body
          )
        : null}
    </span>
  );
}

// ── Icons (inherit currentColor, 16px) ──────────────────────────────────────

function PhoneIcon() {
  return (
    <svg
      className="contact-icon"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg
      className="contact-icon"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M17.47 14.38c-.29-.15-1.7-.84-1.96-.93-.26-.1-.45-.15-.64.14-.19.29-.74.93-.9 1.12-.17.19-.33.21-.62.07-.29-.15-1.22-.45-2.33-1.44-.86-.77-1.44-1.72-1.61-2.01-.17-.29-.02-.44.13-.59.13-.13.29-.34.43-.51.15-.17.19-.29.29-.48.1-.19.05-.36-.02-.51-.07-.14-.64-1.55-.88-2.12-.23-.55-.47-.48-.64-.49l-.55-.01c-.19 0-.5.07-.76.36-.26.29-1 .98-1 2.38s1.03 2.76 1.17 2.95c.14.19 2.02 3.08 4.9 4.32.68.29 1.22.47 1.63.6.69.22 1.31.19 1.8.11.55-.08 1.7-.69 1.94-1.36.24-.67.24-1.24.17-1.36-.07-.12-.26-.19-.55-.34zM12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.86 9.86 0 0 0 12.04 2zm0 18.15h-.01a8.2 8.2 0 0 1-4.19-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.24 8.24 0 0 1-1.26-4.38c0-4.54 3.7-8.23 8.24-8.23 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.82c0 4.54-3.69 8.23-8.23 8.23z" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg
      className="contact-icon"
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </svg>
  );
}

function DotsIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="currentColor"
      aria-hidden="true"
    >
      <circle cx="5" cy="12" r="1.6" />
      <circle cx="12" cy="12" r="1.6" />
      <circle cx="19" cy="12" r="1.6" />
    </svg>
  );
}
